// Copyright © 2015-2020 TrampleZone LLC All Rights Reserved

// AdMobPlatform Module for the Google AdMob ad network

// This module provides support for advertising in the Client application
// when running as a mobile app.  It supports both banner and interstitials.
//
// Support for throttling display rate is also provided.  For example,
// one can decide to only show an ad in the slot a certain percentage of the
// time.  This is important to be responsive to user input and maximize
// ad revenue and user satisfaction.
//

define('AdMobPlatform',['require','Policies','Utilities'],function (require) {
    'use strict';

    var // Require.js module dependencies
        Policies = require('Policies'),
        Utilities = require('Utilities'),

        // Common
        policies,
        utilities,
        platformName = 'AdMob',     // Name of the ad platform in use
        mobileOS,                   // The mobile OS in use (ios/android...)
        initialized,                // True if ad platform was initialized
        isAdShowing,                // True if an ad is currently showing
        defaultBannerOptions,       // Default banner options for ads
        bannerRate = 1.0,           // How frequently to show banner ads
        interstitialRate = 0.3,     // How frequently to show interstitials
        publisherId,                // Publisher ID for advertising
        adSlots,                    // Advertising credentials

        // Supported operating systems
        supportedMobileOS = [
            'ios',
            'android',
        ],

        // Other
        main;


    // Constructor
    function AdMobPlatform () {

        // Dependent resources
        policies = new Policies();
        utilities = new Utilities();

        // Public Methods

        // Set Google Publisher ID
        this.setPublisherId = function () {
            publisherId = policies.getPolicy('googlePublisherId');
        };

        // Set Ad Slots
        this.setAdSlots = function () {
            // AdMob credentials
            var adMobPluginProLicense =
                    policies.getPolicy('adMobPluginProLicense') || '';

            adSlots = {
                ios: {
                    banner: {
                        adUnit:         publisherId + '/' + policies.getPolicy(
                                            'adMobIosBannerAdUnit') || '',
                        displayRate:    bannerRate,
                        license:        adMobPluginProLicense,
                        adReady:        false
                    },
                    interstitial: {
                        adUnit:         publisherId + '/' + policies.getPolicy(
                                            'adMobIosInterstitialAdUnit') || '',
                        displayRate:    interstitialRate,
                        license:        adMobPluginProLicense,
                        adReady:        false
                    }
                },
                android: {
                    banner: {
                        adUnit:         publisherId + '/' + policies.getPolicy(
                                            'adMobAndroidBannerAdUnit') || '',
                        displayRate:    bannerRate,
                        license:        adMobPluginProLicense,
                        adReady:        false
                    },
                    interstitial: {
                        adUnit:         publisherId + '/' + policies.getPolicy(
                                            'adMobAndroidInterstitialAdUnit') || '',
                        displayRate:    interstitialRate,
                        license:        adMobPluginProLicense,
                        adReady:        false
                    }
                }
            };
        };

        // Set the display rate for banner ads
        this.setBannerDisplayRate = function (newBannerRate, os) {
            // If OS specified, only update the mobile os specific rate
            if (os && supportedMobileOS.indexOf(os) >= 0) {
                adSlots[os].banner.displayRate = newBannerRate;
            } else {
                // Set the default display rate for all platforms
                bannerRate = newBannerRate;
                for (var i=0; i<supportedMobileOS.length; i++) {
                    adSlots[supportedMobileOS[i]].banner.displayRate =
                        bannerRate;
                }
            }
        };

        // Set the display rate for interstitial ads
        this.setInterstitialDisplayRate = function (newInterstitialRate, os) {
            // If OS specified, only update the mobile os specific rate
            if (os && supportedMobileOS.indexOf(os) >= 0) {
                adSlots[os].interstitial.displayRate = newInterstitialRate;
            } else {
                // Set the default display rate for all platforms
                interstitialRate = newInterstitialRate;
                for (var i=0; i<supportedMobileOS.length; i++) {
                    adSlots[supportedMobileOS[i]].interstitial.displayRate =
                        interstitialRate;
                }
            }
        };

        // Set ad display Rates based on policies
        this.setDisplayRates = function () {

            // Set banner ad display rate
            var newBannerRate =
                    policies.getPolicy('adDisplayRateBanner'),
                newBannerRateIOS =
                    policies.getPolicy('adDisplayRateBannerIOS'),
                newBannerRateAndroid =
                    policies.getPolicy('adDisplayRateBannerAndroid');
            if (newBannerRate) {
                this.setBannerDisplayRate(parseFloat(newBannerRate));
            }
            if (newBannerRateIOS) {
                this.setBannerDisplayRate(
                    parseFloat(newBannerRateIOS), 'ios');
            }
            if (newBannerRateAndroid) {
                this.setBannerDisplayRate(
                    parseFloat(newBannerRateAndroid), 'android');
            }

            // Set interstitial ad display rate
            var newInterstitialRate =
                    policies.getPolicy('adDisplayRateInterstitial'),
                newInterstitialRateIOS =
                    policies.getPolicy('adDisplayRateInterstitialIOS'),
                newInterstitialRateAndroid =
                    policies.getPolicy('adDisplayRateInterstitialAndroid');
            if (newInterstitialRate) {
                this.setInterstitialDisplayRate(parseFloat(newInterstitialRate));
            }
            if (newInterstitialRateIOS) {
                this.setInterstitialDisplayRate(
                    parseFloat(newInterstitialRateIOS), 'ios');
            }
            if (newInterstitialRateAndroid) {
                this.setInterstitialDisplayRate(
                    parseFloat(newInterstitialRateAndroid), 'android');
            }
        };
    };

    // Public Prototype Methods

    // Initializations
    AdMobPlatform.prototype.initialize = function (newMain) {
        // Save reference to Main.js
        main = newMain;

        // Set advertising credentials from Server policies
        this.setPublisherId();
        this.setAdSlots();

        // Set display rates from Server policies
        this.setDisplayRates();

        // Set initial state
        isAdShowing = false;
        initialized = false;

        // Register credentials and specify options
        if (typeof AdMob != 'undefined') {
            // Determine mobile OS
            mobileOS = utilities.isIOS() ? 'ios' : 'android';
            // Establish ad listener to see if it's ready
            document.addEventListener('onAdLoaded', function (data) {
                if (data.adType == 'banner') {
                    adSlots[mobileOS].banner.adReady = true;
                } else if (data.adType == 'interstitial') {
                    adSlots[mobileOS].interstitial.adReady = true;
                }
            });
            // Register banner credentials
            var admobBannerId = adSlots[mobileOS].banner.adUnit,
                license = adSlots[mobileOS].banner.license;
            // Specify default options
            defaultBannerOptions = {
                license: license,
                adSize: 'SMART_BANNER',
                position: AdMob.AD_POSITION.BOTTOM_CENTER,
                autoShow: false,
                isTesting: false
            };
            // Add create options
            var createBannerOptions = defaultBannerOptions;
            createBannerOptions['adId'] = admobBannerId;
            // Create the initial banner
            AdMob.setOptions(defaultBannerOptions);
            AdMob.createBanner(
                createBannerOptions,
                function () {
                    // Success
                    initialized = true;
                },
                function () {
                    // Failure
                    utilities.log('error',
                                  'AdMob plugin did not initialize!');
                }
            );
        }
    };

    // Show/Hide banner ad
    AdMobPlatform.prototype.showBannerAd = function (
        visible, adDiv, callback) {
        // Only show/hide if necessary
        if (initialized && visible !== isAdShowing) {
            // Show or Hide the banner
            if (visible) {
                // Get a random number between 1 and 10
                var randomItem = Math.floor(Math.random() * (10)) + 1;
                // Determine if an ad should be shown based on rate
                if (randomItem <=
                    (adSlots[mobileOS].banner.displayRate * 10)) {
                    AdMob.showBanner(defaultBannerOptions.position);
                }
                isAdShowing = true;
                if (callback) {
                    callback();
                }
            } else {
                AdMob.hideBanner(
                    // Success
                    function () {
                        isAdShowing = false;
                        if (callback) {
                            callback();
                        }
                    },
                    // Failure
                    function () {
                        if (callback) {
                            callback();
                        }
                    }
                );
            }
        } else {
            // Call the callback if there is one
            if (callback) {
                callback();
            }
        }
    };

    // Download interstitial ad based on platform name
    AdMobPlatform.prototype.prepareInterstitialAd = function () {
        // Reset readiness flag
        adSlots[mobileOS].interstitial.adReady = false;
        // Get a random number between 1 and 10
        var randomItem = Math.floor(Math.random() * (10)) + 1;
        // Determine if an ad should be shown based on rate
        if (randomItem <=
            (adSlots[mobileOS].interstitial.displayRate * 10)) {
            // Prepare an interstitial ad
            AdMob.prepareInterstitial({
                adId: adSlots[mobileOS].interstitial.adUnit,
                license: adSlots[mobileOS].interstitial.license,
                autoShow: false
            });
        }
    };

    // Show interstitial ad based on platform name
    AdMobPlatform.prototype.showInterstitialAd = function () {
        // Show the interstitial if it's ready
        if (adSlots[mobileOS].interstitial.adReady) {
            AdMob.showInterstitial();
            adSlots[mobileOS].interstitial.adReady = false;
        }
    };

    // Get Ad platform name
    AdMobPlatform.prototype.getAdPlatform = function () {
        return platformName;
    };

    // Return Constructor
    return AdMobPlatform.prototype.constructor;
});

